import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import ForgotPassword from './ForgotPassword';
import { useAuth } from '../../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, defaultMode = 'login', onAuthSuccess }) => {
  const [mode, setMode] = useState(defaultMode);
  const { login, signup, isLoading, isAuthenticated } = useAuth();

  // Reset mode when modal closes, but preserve it while open
  useEffect(() => {
    if (!isOpen) {
      // Reset mode when modal closes
      setMode(defaultMode);
    }
  }, [isOpen, defaultMode]);

  // Auto-close modal when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      console.log('🏠 MODAL: User authenticated, auto-closing modal');
      onClose();
      if (onAuthSuccess) {
        console.log('🏠 MODAL: Calling onAuthSuccess callback');
        onAuthSuccess();
      }
    }
  }, [isAuthenticated, isOpen, onClose, onAuthSuccess]);

  const handleLogin = async (credentials) => {
    console.log('🏠 MODAL: handleLogin called with credentials:', { ...credentials, password: '***' });
    try {
      console.log('🏠 MODAL: Calling login from context...');
      const result = await login(credentials);
      console.log('🏠 MODAL: Login successful! Result:', { ...result, token: result?.token ? '***' : undefined });

      // Debug localStorage immediately after login
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      console.log('🏠 MODAL: Checking localStorage after login:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        tokenLength: storedToken?.length,
        userDataExists: !!storedUser
      });

      console.log('🏠 MODAL: Login completed! Modal will close via useEffect when isAuthenticated becomes true');
      // Don't close modal here - let the useEffect handle it when isAuthenticated becomes true
      // This way if login fails, the modal stays open to show errors
    } catch (error) {
      console.error('🏠 MODAL: Login failed, error will be shown in form:', error);
      // Don't close modal on error - let the LoginForm display the error
      throw error;
    }
  };

  const handleSignup = async (userData) => {
    console.log('🏠 MODAL v3: handleSignup called with data:', { ...userData, password: '***', confirmPassword: '***' });
    try {
      console.log('🏠 MODAL v3: Calling signup from context...');
      await signup(userData);
      console.log('🏠 MODAL v3: Signup successful! Modal will close via useEffect when isAuthenticated becomes true');
      // Don't close modal here - let the useEffect handle it when isAuthenticated becomes true
      // This way if signup fails, the modal stays open to show errors
    } catch (error) {
      console.error('🏠 MODAL v3: Signup failed, error will be shown in form:', error);
      console.error('🏠 MODAL v3: NOT closing modal - error should display in form');
      // Don't close modal on error - let the SignupForm display the error
      // Re-throw so the form can catch and display it
      throw error;
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="modal-close"
          aria-label="Close modal"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path 
              d="M18 6L6 18" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M6 6L18 18" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {mode === 'login' ? (
          <LoginForm
            onLogin={handleLogin}
            onSwitchToSignup={() => setMode('signup')}
            onSwitchToForgotPassword={() => setMode('forgotPassword')}
            isLoading={isLoading}
          />
        ) : mode === 'signup' ? (
          <SignupForm
            onSignup={handleSignup}
            onSwitchToLogin={() => setMode('login')}
            isLoading={isLoading}
          />
        ) : (
          <ForgotPassword
            isOpen={true}
            onClose={onClose}
            onBackToLogin={() => setMode('login')}
          />
        )}
      </div>
    </div>
  );
};

export default AuthModal;